import React, { useEffect, useState } from "react";
// External dependencies
import classNames from "classnames";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
// Internal Dependencies
import { CustomMonthPickerWrapper } from "./CustomMonthPickerWrapper.style";
import { VersionData } from "../../pages/Version/Version";
import { z } from "zod";
import { axiosFunction } from "../commonFunctions/axiosFunction";

// Zod schema to validate the API response
const ChangeLogItemDTO = z.object({
  title: z.string(),
  changeLogItemId: z.number(), 
});

const ChangeLogDTO = z.object({
  version: z.string(),
  createdOn: z.string(), 
  changeLogId: z.number(), 
  deployedDateTime: z.string(),
});

const ApiResponseSchema = z.object({
  status: z.boolean(),
  message: z.string().nullable(),
  response:
    z.array(
      z.object({
        changeLogDTO: ChangeLogDTO,
        changeLogItemDTOs: z.array(ChangeLogItemDTO),
      })
    ),
});

interface MonthPickerProps {
  setlastUpdated: (message: string | "") => void; 
  setVersionData: (data: VersionData[]) => void; 
  className?: string; 
}

const MonthPicker: React.FC<MonthPickerProps> = ({
  setlastUpdated,
  setVersionData,
  className,
}) => {
  // Combining default and additional classes for the main classes
  const mainClasses = classNames(
    "custom-datepicker form-control form-control-sm mr-1",
    className
  );

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  useEffect(() => {
    fetchData(new Date());
  }, []);

  const fetchData = (date: Date | null) => {
    const selectedDate = moment(date).format("MM-yyyy");
     axiosFunction(
      `/changelog/json/react?date=${selectedDate}`,
      "GET",
    ).then((result: { data: unknown; }) => {
        const validation = ApiResponseSchema.safeParse(result.data);
        if (validation.success) {
          const { message, response } = validation.data;
          setlastUpdated(message && message.includes("null") ? "" : message || "");
          setVersionData(response as VersionData[]);
        } else {
          console.error("API response validation failed:", validation.error);
        }
      })
      .catch((error: any) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      fetchData(date);
    }
  };

  // Function to get the current year
  const getCurrentYear = () => {
    const today = new Date();
    return today.getFullYear();
  };

  return (
    <CustomMonthPickerWrapper>
      <ReactDatePicker
        showIcon
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        placeholderText="Select month"
        icon="fa fa-calendar"
        yearDropdownItemNumber={getCurrentYear() - 2021}
        minDate={new Date("2019-01-01")}
        closeOnScroll={true}
        maxDate={new Date()}
        className={mainClasses}
      />
    </CustomMonthPickerWrapper>
  );
};

export default MonthPicker;
